/**
 * Cucine da Incubo, who closed and who is still open: the page at
 * `/cucine-da-incubo-chiusi-e-aperti`.
 *
 * People search what became of the restaurants Cannavacciuolo visited ("cucine
 * da incubo ristoranti chiusi", "... ancora aperti", "che fine hanno fatto")
 * far more than any single episode. Shared by the app and the Worker, as
 * `src/puntate.js` is: the groups and their words must be the same in both.
 * Nothing here touches the DOM.
 */

export const STATUS_PROGRAM = 'cucine-da-incubo';
export const STATUS_PATH = '/cucine-da-incubo-chiusi-e-aperti';
export const STATUS_TITLE = 'Cucine da Incubo: i ristoranti chiusi e quelli ancora aperti';
export const STATUS_LINK = 'Chi ha chiuso e chi è ancora aperto';

/**
 * Not found on Google Maps nor anywhere else on 21 October 2026, all aired in
 * 2013-2018. Named one by one on purpose: any other venue without a verdict —
 * a new episode above all — is one we know nothing about, not one that is gone.
 */
const GONE = new Set([
  'al-bacioch',
  'cuore-e-sapore',
  'il-ritrovo-del-gusto-con-chef-silvano',
  'isola-fiorita',
  'la-croce-del-gallo',
  'la-zuccona',
  'le-mille-bonta',
  'muntisel',
  'vita-nova',
]);

/** One of the venues nobody could find any more (`GONE`). */
export function isGone(venue) {
  return venue.stato === 'ignoto' && GONE.has(venue.id);
}

const byAiredDesc = (a, b) => (b.puntata?.data ?? '').localeCompare(a.puntata?.data ?? '');

/**
 * The programme's venues in four groups, each from the most recent episode;
 * empty groups are left out.
 */
export function statusGroups(venues) {
  const groups = [
    { key: 'chiusi', label: 'Hanno chiuso', note: null, venues: [] },
    { key: 'aperti', label: 'Ancora aperti', note: null, venues: [] },
    {
      key: 'spariti',
      label: 'Non risultano più attivi',
      note: 'Non li troviamo né su Google Maps né altrove: con ogni probabilità hanno chiuso.',
      venues: [],
    },
    {
      key: 'incerti',
      label: 'Non sappiamo se sono ancora aperti',
      note: 'Non abbiamo notizie sicure: se ci sei stato di recente, segnalacelo dalla scheda del locale.',
      venues: [],
    },
  ];
  const [closed, open, gone, unsure] = groups;
  for (const venue of venues) {
    if (venue.stato === 'chiuso' || venue.stato === 'chiuso_temporaneamente') closed.venues.push(venue);
    else if (venue.stato === 'aperto') open.venues.push(venue);
    else if (GONE.has(venue.id)) gone.venues.push(venue);
    else unsure.venues.push(venue);
  }
  for (const group of groups) group.venues.sort(byAiredDesc);
  return groups.filter((group) => group.venues.length);
}

function count(groups, key) {
  return groups.find((group) => group.key === key)?.venues.length ?? 0;
}

/** "46 ancora aperti · 30 hanno chiuso · 9 non più attivi · 4 incerti". */
export function statusSub(groups) {
  return [
    [count(groups, 'aperti'), 'ancora aperti'],
    [count(groups, 'chiusi'), 'hanno chiuso'],
    [count(groups, 'spariti'), 'non più attivi'],
    [count(groups, 'incerti'), 'incerti'],
  ]
    .filter(([n]) => n)
    .map(([n, words]) => `${n} ${words}`)
    .join(' · ');
}

export function statusIntro(total) {
  const article = /^(8|11)/.test(String(total)) ? 'gli' : 'i';
  return (
    `Che fine hanno fatto ${article} ${total} ristoranti passati da Antonino Cannavacciuolo? ` +
    'Per ognuno lo stato controllato su Google Maps.'
  );
}

/** The page's title and description, for the Worker and for the tab's title. */
export function statusHead(groups) {
  const closed = count(groups, 'chiusi');
  const open = count(groups, 'aperti');
  return {
    title: STATUS_TITLE,
    description:
      'Che fine hanno fatto i ristoranti di Cucine da Incubo con Antonino Cannavacciuolo: ' +
      `${closed} hanno chiuso, ${open} sono ancora aperti. ` +
      'Per ognuno lo stato verificato su Google Maps, con la data.',
  };
}
